import React from 'react';
import { View, Pressable, StyleProp, ViewStyle } from 'react-native';
import { Bevel } from './Bevel';
import { Text } from './Text';
import { makeStyles, useTheme } from './ThemeContext';

/* ------------------------------------------------------------------ MenuBar */

export interface MenuItem {
  label: string;
  onPress?: () => void;
  disabled?: boolean;
}

/**
 * The flat menu strip under a caption bar. Real Win95 menus dropped down; this
 * fires on press because the vertical slice has no popover layer yet, and on
 * touch a drop-down would need a different interaction anyway.
 */
export function MenuBar({ items, style }: { items: MenuItem[]; style?: StyleProp<ViewStyle> }) {
  const styles = useStyles();
  return (
    <View style={[styles.menuBar, style]}>
      {items.map((item) => (
        <MenuBarItem key={item.label} item={item} />
      ))}
    </View>
  );
}

function MenuBarItem({ item }: { item: MenuItem }) {
  const styles = useStyles();
  const [held, setHeld] = React.useState(false);
  const open = held && !item.disabled;
  return (
    <Pressable
      onPress={item.disabled ? undefined : item.onPress}
      onPressIn={() => setHeld(true)}
      onPressOut={() => setHeld(false)}
      disabled={item.disabled}
      accessibilityRole="menuitem"
      accessibilityLabel={item.label}
      style={[styles.menuItem, open && styles.menuItemOpen]}
    >
      <Text
        disabled={item.disabled}
        style={open ? styles.menuItemOpenText : undefined}
      >
        {item.label}
      </Text>
    </Pressable>
  );
}

/* -------------------------------------------------------------- ProgressBar */

/**
 * The chunky segmented progress bar, used by the sign-on sequence.
 * `value` is 0..1; segments fill discretely, exactly like the original.
 */
export function ProgressBar({ value, style }: { value: number; style?: StyleProp<ViewStyle> }) {
  const styles = useStyles();
  const clamped = Math.max(0, Math.min(1, value));
  const total = 20;
  const filled = Math.round(clamped * total);
  return (
    <Bevel
      variant="sunken"
      style={[styles.progress, style]}
      contentStyle={styles.progressInner}
      accessibilityRole="progressbar"
      accessibilityValue={{ min: 0, max: 100, now: Math.round(clamped * 100) }}
    >
      {Array.from({ length: total }, (_, i) => (
        <View key={i} style={[styles.chunk, i < filled && styles.chunkFilled]} />
      ))}
    </Bevel>
  );
}

/* ----------------------------------------------------------------- Checkbox */

export function Checkbox({
  checked,
  onChange,
  label,
  disabled,
  style,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
  style?: StyleProp<ViewStyle>;
}) {
  const styles = useStyles();
  const theme = useTheme();
  return (
    <Pressable
      onPress={disabled ? undefined : () => onChange(!checked)}
      disabled={disabled}
      accessibilityRole="checkbox"
      accessibilityState={{ checked, disabled: !!disabled }}
      accessibilityLabel={label}
      style={[styles.checkRow, style]}
      hitSlop={6}
    >
      <Bevel
        variant="sunken"
        background={disabled ? theme.colors.face : theme.colors.fieldBg}
        style={styles.checkBox}
        contentStyle={styles.checkInner}
      >
        {checked ? <CheckGlyph disabled={disabled} /> : null}
      </Bevel>
      <Text disabled={disabled} style={styles.checkLabel}>
        {label}
      </Text>
    </Pressable>
  );
}

/** The 7x7 bitmap check mark, drawn as two rotated strokes. */
function CheckGlyph({ disabled }: { disabled?: boolean }) {
  const styles = useStyles();
  const theme = useTheme();
  const c = disabled ? theme.colors.disabledText : theme.colors.windowText;
  return (
    <View style={styles.checkGlyph}>
      <View style={[styles.checkShort, { backgroundColor: c }]} />
      <View style={[styles.checkLong, { backgroundColor: c }]} />
    </View>
  );
}

/* ------------------------------------------------------------------ Divider */

/** Etched horizontal rule, as used between groups in AIM's buddy list. */
export function Divider({ style }: { style?: StyleProp<ViewStyle> }) {
  const styles = useStyles();
  return (
    <View style={[styles.divider, style]}>
      <View style={styles.dividerTop} />
      <View style={styles.dividerBottom} />
    </View>
  );
}

/* ---------------------------------------------------------------- GroupBox */

export function GroupBox({
  title,
  children,
  style,
}: {
  title: string;
  children: React.ReactNode;
  style?: StyleProp<ViewStyle>;
}) {
  const styles = useStyles();
  return (
    <View style={[styles.groupBox, style]}>
      <Bevel variant="thin" style={styles.groupFrame} contentStyle={styles.groupInner}>
        {children}
      </Bevel>
      <View style={styles.groupTitle}>
        <Text>{title}</Text>
      </View>
    </View>
  );
}

const useStyles = makeStyles((t) => ({
  menuBar: { flexDirection: 'row' as const, backgroundColor: t.colors.face, paddingVertical: 1 },
  menuItem: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    minHeight: t.metrics.touchTarget / 2,
    justifyContent: 'center' as const,
  },
  menuItemOpen: { backgroundColor: t.colors.selectionBg },
  menuItemOpenText: { color: t.colors.selectionText },

  progress: { height: 18 },
  progressInner: { flexDirection: 'row' as const, alignItems: 'stretch' as const, padding: 2, gap: 1 },
  chunk: { flex: 1, backgroundColor: 'transparent' },
  chunkFilled: { backgroundColor: t.colors.selectionBg },

  checkRow: { flexDirection: 'row' as const, alignItems: 'center' as const, minHeight: t.metrics.touchTarget / 2 },
  checkBox: { width: 13, height: 13 },
  checkInner: { alignItems: 'center' as const, justifyContent: 'center' as const },
  checkLabel: { marginLeft: 5 },
  checkGlyph: { width: 9, height: 9 },
  checkShort: { position: 'absolute' as const, width: 4, height: 2, left: 0, top: 4, transform: [{ rotate: '45deg' }] },
  checkLong: { position: 'absolute' as const, width: 8, height: 2, left: 2, top: 3, transform: [{ rotate: '-45deg' }] },

  divider: { height: 2, alignSelf: 'stretch' as const },
  dividerTop: { height: 1, backgroundColor: t.colors.shadow },
  dividerBottom: { height: 1, backgroundColor: t.colors.highlight },

  groupBox: { paddingTop: 7 },
  groupFrame: { flex: 1 },
  groupInner: { padding: 8 },
  groupTitle: {
    position: 'absolute' as const,
    left: 8,
    top: 0,
    backgroundColor: t.colors.face,
    paddingHorizontal: 3,
  },
}));
